import { format, parse, isValid, parseISO } from "date-fns";
import { isNil } from "lodash";
const DATE_TYPES = Object.freeze({
    longDate: "longDate",
    shortDateTime: "shortDateTime",
});

const getInitialDates = (diff) => {
    let today = getDateWithoutTime(new Date());
    let prev = getDateWithoutTime(new Date());
    let currentMonth = today.getMonth();
    let prevMonth = currentMonth - diff;
    prev.setMonth(prevMonth);
    today.setDate(today.getDate() + 1);
    return {
        start: prev.toISOString(),
        end: today.toISOString(),
    };
};

const getDateFromSeconds = (seconds) => {
    return new Date(seconds * 1000);
};

const getDatePlusMonths = (months) => {
    let today = getDateWithoutTime(new Date());
    let future = getDateWithoutTime(new Date());
    let currentMonth = today.getMonth();
    let futureMonth = currentMonth + months;
    future.setMonth(futureMonth);
    return {
        start: shortDateYMD(today),
        end: shortDateYMD(future),
    };
};

const getDateWithoutTime = (dateTime) => {
    if (dateTime) {
        var date = new Date(dateTime.getTime());
        date.setHours(0, 0, 0, 0);
        return date;
    }
};

const getCurrentDateYMD = (formatStr = null) => {
    const currentDate = shortDateYMD(new Date());
    if (formatStr) return format(currentDate, formatStr);
    return currentDate;
};

//shortDateYMD => 2020-01-30
const shortDateYMD = (date, addDays = 0) => {
    let newDate = new Date(date);
    newDate.setDate(newDate.getDate() + addDays);
    let stringDate = [
        newDate.getFullYear(),
        ("0" + (newDate.getMonth() + 1)).slice(-2),
        ("0" + newDate.getDate()).slice(-2),
    ].join("-");
    return stringDate;
};

const getDateTimeWithTimeZone = (date) => {
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000 * -1);
};

const checkDateFormat = (date) => {
    let d = new Date(date);
    if (date && typeof date === "string" && !date.includes("T"))
        d = getDateTimeWithTimeZone(d);
    return d;
};

//longDateTime => January 1, 2022, 02:00 AM
const longDateTime = (date) => {
    if (isNil(date)) return "";
    try {
        const DateOptions = {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        };
        const d = checkDateFormat(date);

        return d.toLocaleDateString("en-US", DateOptions);
    } catch (error) {
        return new Date().toLocaleDateString("en-US", DateOptions);
    }
};

const shortDateTime = (date) => {
    try {
        const DateOptions = {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        };
        const d = checkDateFormat(date);

        return d.toLocaleDateString("en-US", DateOptions);
    } catch (error) {
        return new Date().toLocaleDateString("en-US", DateOptions);
    }
};

// This validate and parse the data to date-fns format
const isValidDateString = (dateString) => {
    if (!dateString) return null;

    // Try parsing as ISO 8601 format
    const isoDate = parseISO(dateString);

    if (isValid(isoDate)) {
        return isoDate; // Valid ISO 8601 date
    }

    // Try parsing as custom format 'MM-dd-yyyy'
    const customFormatDate = parse(dateString, "MM-dd-yyyy", new Date());

    if (isValid(customFormatDate)) {
        return customFormatDate; // Valid custom format
    }

    if (isValid(new Date(dateString))) {
        return new Date(dateString);
    }

    // Date string is not in a valid format
    return null;
};

//longDate => August 1, 2023
const longDate = (date, formatStr = "MMMM d, yyyy") => {
    try {
        const dateString = isValidDateString(date);
        if (!dateString) return "";

        // Format the parsed date as "MMMM d, yyyy"
        const formattedDate = format(dateString, formatStr);
        return formattedDate.toString();
    } catch (error) {
        return "";
    }
};

const formatDateToYMD = (inputDateString, formatStr = "MM-dd-yyyy") => {
    try {
        if (!inputDateString) return null;

        // Parse the input date string with the 'dd-MM-yyyy' format
        const parsedDate = parse(inputDateString, formatStr, new Date());

        // Format the parsed date as 'yyyy-MM-dd'
        const formattedDate = format(parsedDate, "yyyy-MM-dd");
        return formattedDate;
        // eslint-disable-next-line no-empty
    } catch (error) {}

    return null;
};

const getDateRange = (startDate, endDate, difference) => {
    let max = "";
    let min = "";

    if (startDate) {
        max = new Date(startDate);
        max = shortDateYMD(calculateDateMonth(max, difference, true));
    }

    if (endDate) {
        min = new Date(endDate);
        min = shortDateYMD(calculateDateMonth(min, difference, false));
    }

    return { min, max };
};

const calculateDateMonth = (date, difference, isAddition) => {
    let currentDate = new Date();
    let newDate = new Date(date);
    isAddition
        ? newDate.setMonth(newDate.getMonth() + difference)
        : newDate.setMonth(newDate.getMonth() - difference);
    if (newDate > currentDate) return currentDate;
    else return newDate;
};

const addDateToDate = (
    date = new Date(),
    dayChange = 0,
    monthChange = 0,
    yearChange = 0
) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    return new Date(year + yearChange, month + monthChange, day + dayChange);
};

const getTimezoneoffset = () => {
    return new Date().getTimezoneOffset() / -60;
};

const getNumberOfDaysBetweenDates = (
    date1 = new Date(),
    date2 = new Date()
) => {
    const diffInTime = date1.getTime() - date2.getTime();
    return Math.abs(diffInTime / (1000 * 3600 * 24));
};

const getFilterDate = (date, isDayStart = true) => {
    if (date) {
        let newDate = new Date(date);

        const timezoneHours = getTimezoneoffset();
        if (isDayStart) {
            newDate.setHours(newDate.getHours() - timezoneHours);
            newDate.setMinutes(0);
        } else {
            newDate.setHours(newDate.getHours() - timezoneHours);
            newDate.setHours(23);
            newDate.setMinutes(59);
        }

        return newDate;
    }
};

const getPayloadDate = (dateValue) => {
    if (!dateValue) return dateValue;
    return format(parseISO(dateValue), "MM-dd-yyyy");
};

export {
    DATE_TYPES,
    getInitialDates,
    getDateWithoutTime,
    longDateTime,
    shortDateYMD,
    longDate,
    shortDateTime,
    getDateRange,
    calculateDateMonth,
    getDateFromSeconds,
    getCurrentDateYMD,
    getDatePlusMonths,
    addDateToDate,
    getTimezoneoffset,
    getNumberOfDaysBetweenDates,
    getFilterDate,
    formatDateToYMD,
    getPayloadDate,
};
